import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Newspaper, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { RichTextEditor } from '@/components/shared/RichTextEditor';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  createNews,
  getAllNews,
  deleteNews,
  getDropDownFirms,
  updateNews
} from '@/services/newsApi';

export const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNews, setSelectedNews] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [deleteNewsItem, setDeleteNewsItem] = useState(null);

  // Form state
  const [firmId, setFirmId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState('Draft');

  // ==================== FETCH DATA ====================
  const fetchNews = async () => {
  try {
    setLoading(true);
    const data = await getAllNews();
    setNews(Array.isArray(data) ? data : data?.data || []);
  } catch (error) {
    console.error(error);
    toast.error('Failed to load news');
  } finally {
    setLoading(false);
  }
};

const fetchFirms = async () => {
  try {
    const data = await getDropDownFirms();
    setFirms(Array.isArray(data) ? data : data?.data || []);
  } catch (error) {
    console.error(error);
    toast.error('Failed to load firms for dropdown');
  }
};

useEffect(() => {
  fetchNews();
  fetchFirms();
}, []);

  useEffect(() => {
    fetchNews();
    fetchFirms();
  }, []);

  // ==================== FORM HELPERS ====================
  const resetForm = () => {
    setFirmId('');
    setTitle('');
    setBody('');
    setStatus('Draft');
  };

  const openCreate = () => {
    resetForm();
    setIsAddingNew(true);
    setSelectedNews(null);
  };

  const openEdit = (item) => {
    setSelectedNews(item);
    setIsAddingNew(false);

    setFirmId(item.firm_id || item.firmId || '');
    setTitle(item.title || '');
    setBody(item.body || '');
    setStatus(item.status || 'Draft');
  };

  const closeSheet = () => {
    setIsAddingNew(false);
    setSelectedNews(null);
    resetForm();
  };

  // ==================== BUILD PAYLOAD ====================
  const buildPayload = (includeId = false) => {
  const payload = {
    firm_id: firmId,
    title,
    body,
    status, // "Draft" | "Published"
  };

  if (includeId && selectedNews?.id) {
    payload.id = selectedNews.id;
  }

  return payload;
};

const handleSave = async () => {
  if (!firmId) {
    toast.error('Please select a firm');
    return;
  }
  if (!title.trim()) {
    toast.error('Title is required');
    return;
  }
  if (!body.trim()) {
    toast.error('Body is required');
    return;
  }

  setSaving(true);

  try {
    if (isAddingNew) {
      await createNews(buildPayload(false));
      toast.success('News item created successfully');
    } else {
      // { id, firm_id, title, body, status }
      await updateNews(buildPayload(true));
      toast.success('News item updated successfully');
    }

    closeSheet();
    fetchNews();
  } catch (error) {
    console.error(error);
    toast.error(error?.response?.data?.message || 'Failed to save news item');
  } finally {
    setSaving(false);
  }
};
  // ==================== DELETE ====================
  const handleDelete = async () => {
    if (!deleteNewsItem) return;

    try {
      await deleteNews(deleteNewsItem.id);
      toast.success('News item deleted');
      setDeleteNewsItem(null);
      fetchNews();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete news item');
    }
  };

  // ==================== FILTER ====================
  const filteredNews = news.filter((item) => {
    const firm = firms.find((f) => f.id === (item.firm_id || item.firmId));
    return (
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      firm?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div>
      <PageHeader
        title="News"
        description="Manage firm-specific news and updates"
        breadcrumbs={['Admin', 'Content', 'News']}
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add News Item
          </Button>
        }
      />

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search news..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Firm
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Title
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Snippet
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Published
                      </TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Status
                      </TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredNews.map((item) => {
                      const firm = firms.find(
                        (f) => f.id === (item.firm_id || item.firmId)
                      );
                      return (
                        <TableRow key={item.id} className="hover:bg-slate-50">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {firm?.logo && (
                                <img
                                  src={firm.logo}
                                  alt=""
                                  className="h-6 w-6 rounded"
                                />
                              )}
                              <span className="text-sm font-medium text-slate-900">
                                {firm?.name || 'Unknown'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm font-medium text-slate-900">
                              {item.title}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600 line-clamp-1 max-w-md">
                              {item.body?.replace(/<[^>]+>/g, '') || ''}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">
                              {item.published_at || item.publishedAt
                                ? format(
                                    new Date(item.published_at || item.publishedAt),
                                    'MMM dd, yyyy'
                                  )
                                : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={item.status?.toLowerCase()} />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEdit(item)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600"
                                onClick={() => setDeleteNewsItem(item)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {filteredNews.length === 0 && (
                <div className="text-center py-12">
                  <Newspaper className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No news items found</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ==================== SHEET ==================== */}
      <Sheet
        open={isAddingNew || !!selectedNews}
        onOpenChange={(open) => {
          if (!open) closeSheet();
        }}
      >
        <SheetContent className="w-[500px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {isAddingNew ? 'Add News Item' : 'Edit News Item'}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Firm */}
            <div>
              <Label>Firm</Label>
              <Select value={firmId} onValueChange={setFirmId}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select firm" />
                </SelectTrigger>
                <SelectContent>
                  {firms.map((firm) => (
                    <SelectItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label>Title</Label>
              <Input
                placeholder="Enter news title"
                className="mt-2"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* Body */}
            <div>
              <Label>Body</Label>
              <div className="mt-2">
                <RichTextEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Write news content..."
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button className="w-full" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isAddingNew ? 'Create News Item' : 'Update News Item'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteNewsItem}
        onOpenChange={() => setDeleteNewsItem(null)}
        title="Delete News Item"
        description={`Are you sure you want to delete "${deleteNewsItem?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};